import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { message } from 'antd';
import { Editor } from '@tinymce/tinymce-react';
import './tinymce-init';
import { getPresetPlugins, getPresetToolbar } from './presets';
import { CONTENT_STYLE } from './contentStyle';
import { stripPastedImageSizeAttributes } from './imagePasteDimensions';
import { sanitizeRichEditorPasteHtml } from './pasteSanitizer';
import './index.less';

/** 将空富文本规范为空串，便于表单校验 */
export function normalizeRichHtml(html) {
    if (html == null || html === '') {
        return '';
    }
    const trimmed = String(html).trim();
    if (trimmed === '' || trimmed === '<p><br></p>' || trimmed === '<p><br/></p>') {
        return '';
    }
    // 仅图片/媒体无文字时，去标签后 textOnly 会为空，不能当成「空富文本」丢弃
    const hasEmbeddedContent = /<\s*(img|video|iframe|embed|object|picture|source)\b/i.test(trimmed);
    const textOnly = trimmed
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim();
    if (!textOnly && !hasEmbeddedContent) {
        return '';
    }
    return html;
}

/**
 * 是否与 normalizeRichHtml 后的「空串」一致，供表单项校验、列表预览等复用
 * （避免仅靠 strip 标签把「仅含图片」误判为空）
 */
export function isEffectivelyEmptyRichHtml(html) {
    return normalizeRichHtml(html) === '';
}

/**
 * 统一富文本编辑器（TinyMCE + React），替代 react-quill 与 window.tinymce.init。
 */
export default function RichEditor({
    mode = 'full',
    value,
    onChange,
    initialValue,
    imageUploadHandler,
    height = 300,
    placeholder,
    disabled = false,
    toolbar: toolbarOverride,
    plugins: pluginsOverride,
    extraInit = {},
    id: idProp,
    medProjectToolbar = false,
    className,
    /** TinyMCE editor 实例 focus，用于问卷等场景的边框与自动保存 */
    onEditorFocus,
    /** TinyMCE editor 实例 blur；第二个参数为规范化后的 HTML（blur 时已 flush，与受控 value 一致） */
    onEditorBlur,
    /** 行内编辑模式（无 iframe，适合题干等嵌入排版；工具栏固定为 simple 预设，可用 toolbar/plugins 覆盖） */
    inline = false,
}) {
    const includeImage = typeof imageUploadHandler === 'function';
    /** 行内编辑不使用 full/standard 大工具栏，避免浮动条过长 */
    const presetMode = inline ? 'simple' : mode;

    const plugins = useMemo(() => {
        if (pluginsOverride != null) {
            return pluginsOverride;
        }
        return getPresetPlugins(presetMode, { includeImage, medProjectStyle: medProjectToolbar });
    }, [pluginsOverride, presetMode, includeImage, medProjectToolbar]);

    const toolbar = useMemo(() => {
        if (toolbarOverride !== undefined && toolbarOverride !== null) {
            return toolbarOverride;
        }
        return getPresetToolbar(presetMode, { includeImage, medProjectStyle: medProjectToolbar });
    }, [toolbarOverride, presetMode, includeImage, medProjectToolbar]);

    const heightCss = typeof height === 'number' ? `${height}px` : height;

    /**
     * 传给 TinyMCE 的受控 HTML 须与 getContent() 一致；onChange 对外仍用 normalizeRichHtml。
     * 若直接用规范化后的 value，tinymce-react 会认为 props.value !== currentContent 而 setContent，导致光标跳动。
     */
    const [editorHtml, setEditorHtml] = useState(() => (value != null ? value : ''));
    const editorHtmlRef = useRef(value != null ? value : '');
    const editorFocusedRef = useRef(false);
    /** 拖拽状态栏调整编辑器高度时 blur 不应把空内容 flush 到表单 */
    const editorChromeResizingRef = useRef(false);
    const onChangeRef = useRef(onChange);
    const onEditorFocusRef = useRef(onEditorFocus);
    const onEditorBlurRef = useRef(onEditorBlur);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);
    useEffect(() => {
        onEditorFocusRef.current = onEditorFocus;
    }, [onEditorFocus]);
    useEffect(() => {
        onEditorBlurRef.current = onEditorBlur;
    }, [onEditorBlur]);

    useEffect(() => {
        if (editorFocusedRef.current) {
            return;
        }
        const next = value != null ? value : '';
        setEditorHtml(prev => {
            if (prev === next) {
                return prev;
            }
            if (normalizeRichHtml(prev) === normalizeRichHtml(next)) {
                return prev;
            }
            editorHtmlRef.current = next;
            return next;
        });
    }, [value]);

    const handleEditorChange = useCallback(content => {
        setEditorHtml(content);
        editorHtmlRef.current = content;
        const next = normalizeRichHtml(content);
        if (onChangeRef.current) {
            onChangeRef.current(next);
        }
    }, []);

    const init = useMemo(() => {
        const { setup: extraSetup, ...restExtra } = extraInit || {};

        const mergedSetup = editor => {
            const endChromeResize = () => {
                editorChromeResizingRef.current = false;
            };

            editor.on('init', () => {
                const container = editor.getContainer?.();
                if (container?.style?.visibility === 'hidden') {
                    container.style.removeProperty('visibility');
                }
            });

            editor.on('mousedown', e => {
                const target = e.target;
                if (target?.closest?.('[data-mce-name="resize-handle"]')) {
                    editorChromeResizingRef.current = true;
                    const view = editor.getDoc()?.defaultView;
                    if (view) {
                        view.addEventListener('mouseup', endChromeResize, { once: true });
                    } else {
                        document.addEventListener('mouseup', endChromeResize, { once: true });
                    }
                }
            });

            editor.on('focus', () => {
                editorFocusedRef.current = true;
                if (typeof onEditorFocusRef.current === 'function') {
                    onEditorFocusRef.current(editor);
                }
            });
            /** blur 往往早于 React 收到最后一次 onEditorChange，先 flush 最新 HTML 再回调，避免受控 value 回退 */
            editor.on('blur', () => {
                editorFocusedRef.current = false;
                if (editorChromeResizingRef.current) {
                    return;
                }
                const raw = editor.getContent();
                const normalized = normalizeRichHtml(raw);
                const prevNormalized = normalizeRichHtml(editorHtmlRef.current);
                if (!normalized && prevNormalized) {
                    return;
                }
                setEditorHtml(raw);
                editorHtmlRef.current = raw;
                if (onChangeRef.current) {
                    onChangeRef.current(normalized);
                }
                if (typeof onEditorBlurRef.current === 'function') {
                    onEditorBlurRef.current(editor, normalized);
                }
            });

            /**
             * TinyMCE 8 对话框焦点修复：
             * TinyMCE 的 dialog 渲染在 document.body 层级（.tox-tinymce-aux sink），
             * 当 RichEditor 嵌套在 Ant Design Drawer/Modal 内时，
             * Drawer 的焦点锁定会阻止焦点移到 DOM 树外的 TinyMCE dialog。
             * 通过在 capturing phase 拦截 focusin 事件，允许焦点进入 TinyMCE dialog。
             */
            let dialogFocusInterceptor = null;

            editor.on('OpenWindow', () => {
                dialogFocusInterceptor = e => {
                    if (e.target.closest('.tox-dialog-wrap')) {
                        e.stopImmediatePropagation();
                    }
                };
                document.addEventListener('focusin', dialogFocusInterceptor, true);

                // 对话框打开后聚焦 textarea
                setTimeout(() => {
                    const ta = document.querySelector('.tox-dialog .tox-textarea');
                    if (ta) {
                        ta.tabIndex = 0;
                        ta.focus();
                    }
                }, 50);
            });

            editor.on('CloseWindow', () => {
                if (dialogFocusInterceptor) {
                    document.removeEventListener('focusin', dialogFocusInterceptor, true);
                    dialogFocusInterceptor = null;
                }
            });

            // 清理 Word/Office 粘贴 HTML 的专有标记，保留可插入的正文内容。
            editor.on('PastePreProcess', e => {
                const result = sanitizeRichEditorPasteHtml(e.content);
                e.content = result.content;
                if (result.removedDisallowedImages) {
                    message.warning('已移除不支持直接粘贴的图片');
                }
            });

            // 粘贴内容里的图片尺寸交给 content_style 的 max-width/auto 控制，不改动已保存内容里的手动尺寸。
            editor.on('PastePostProcess', e => {
                stripPastedImageSizeAttributes(e.node);
            });

            if (typeof extraSetup === 'function') {
                extraSetup(editor);
            }
        };

        const base = {
            /** TinyMCE 8+ 自托管必须声明许可，否则编辑器为只读 */
            license_key: 'gpl',
            branding: false,
            elementpath: false,
            menubar: false,
            placeholder: placeholder || '',
            language: 'zh-Hans',
            language_url: '/langs/zh-Hans.js',
            /** tinymce-init.js 已通过 import 导入皮肤 CSS，设为 false 避免重复加载 */
            skin: false,
            content_css: false,
            content_style: CONTENT_STYLE,
            plugins,
            toolbar,
            /** 图片仍允许手动拖拽缩放；仅粘贴进来的初始尺寸会被清理 */
            object_resizing: true,
            resize_img_proportional: true,
            paste_data_images: false,
            /** TinyMCE 8 toolbar 溢出模式：wrap 会将超出宽度的按钮组换行显示，便于调试溢出问题 */
            toolbar_mode: 'wrap',
            formats: {
                removeformat: [
                    {
                        selector: 'b,strong,em,i,font,u,strike,s,sub,sup,dfn,code,samp,kbd,var,cite,mark,q,del,ins,small',
                        remove: 'all',
                        split: true,
                        block_expand: true,
                        expand: false,
                        deep: true,
                    },
                    { selector: 'span', attributes: ['style', 'class'], remove: 'empty', split: true, expand: false, deep: true },
                    { selector: 'a', attributes: ['style', 'class'], remove: 'empty', split: true, expand: false, deep: true },
                    { selector: 'div,p', attributes: ['style', 'class'], split: false, expand: false, deep: true },
                    { selector: '*', attributes: ['style', 'class'], split: false, expand: false, deep: true },
                ],
            },
            ...restExtra,
            setup: mergedSetup,
        };

        if (inline) {
            // 行内模式：TinyMCE 的 min_height 会撑开可编辑区，问卷题干若传 height=128 会在短文本下出现大块空白；内容仍可随多行自动增高
            const heightNum = typeof height === 'number' ? height : Number.parseInt(String(heightCss).replace(/px$/i, '').trim(), 10);
            const safe = Number.isFinite(heightNum) && heightNum > 0 ? heightNum : 48;
            const inlineMinPx = Math.min(safe, 56);
            base.min_height = inlineMinPx;
        } else {
            base.height = heightCss;
        }

        if (includeImage) {
            base.images_upload_handler = async blobInfo => {
                try {
                    const url = await imageUploadHandler(blobInfo);
                    if (!url) {
                        throw new Error('上传未返回图片地址');
                    }
                    return url;
                } catch (e) {
                    console.error('富文本图片上传失败', e);
                    message.error('图片上传失败');
                    throw e;
                }
            };
        }

        return base;
    }, [height, heightCss, placeholder, plugins, toolbar, extraInit, includeImage, imageUploadHandler, inline]);

    const rootClass = ['boss-rich-editor', inline ? 'boss-rich-editor--inline' : '', className || ''].filter(Boolean).join(' ');

    return (
        <div className={rootClass}>
            {/*
                tinymce-react 在受控模式下若 props.value 与 getContent() 暂不一致会 200ms 后回滚；
                本组件对 onChange 做了 normalize，易与内部 HTML 不同步，需关闭回滚，否则表现为无法持续输入。
            */}
            <Editor id={idProp} inline={inline} disabled={disabled} value={editorHtml} initialValue={initialValue} init={init} onEditorChange={handleEditorChange} rollback={false} />
        </div>
    );
}

RichEditor.propTypes = {
    mode: PropTypes.oneOf(['full', 'standard', 'simple']),
    value: PropTypes.string,
    onChange: PropTypes.func,
    initialValue: PropTypes.string,
    imageUploadHandler: PropTypes.func,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    toolbar: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.array]),
    plugins: PropTypes.string,
    extraInit: PropTypes.object,
    id: PropTypes.string,
    medProjectToolbar: PropTypes.bool,
    className: PropTypes.string,
    onEditorFocus: PropTypes.func,
    onEditorBlur: PropTypes.func,
    inline: PropTypes.bool,
};
